// frontend/src/components/client/Dashboard.js
// Changes from previous version:
//   • handleStatusSubmit now POSTs a real multipart/form-data request to
//     POST /api/client/submit-documents
//   • The file input is now a controlled ref so we can grab the FileList
//   • Error handling shows a toast + keeps the form open so the user can retry
//   • All other UI/logic is unchanged

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { clientAPI, api } from '../../services/api';
import Loading from '../common/Loading';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showWarrantModal, setShowWarrantModal] = useState(false);
  const [warrantZoom, setWarrantZoom] = useState(1);
  const [warrantDocIndex, setWarrantDocIndex] = useState(0);
  const [profile, setProfile] = useState(null);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStatusTemplate, setShowStatusTemplate] = useState(false);
  const [showHardshipModal, setShowHardshipModal] = useState(false);
  const [hardshipStatus, setHardshipStatus] = useState('idle');
  const [statusUpdateStatus, setStatusUpdateStatus] = useState('idle');
  const [selfieUrl, setSelfieUrl] = useState(null);
  // ── NEW: ref to grab files from the file input ────────────────────────────
  const supportingDocsRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // ── NEW: load the ID-verification selfie as an authenticated blob ─────────
  // (the /identity/document/:fileId route requires a Bearer token, which a
  // plain <img src> can't send, so it has to be fetched via the api client)
  useEffect(() => {
    const selfieId = profile?.user?.selfie;
    if (!selfieId) return;

    let objectUrl;
    api.get(`/identity/document/${selfieId}`, { responseType: 'blob' })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        setSelfieUrl(objectUrl);
      })
      .catch((error) => {
        console.error('Failed to load selfie:', error);
      });

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [profile?.user?.selfie]);


  const parseLocalDate = (d) => {
    if (!d) return null;
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [y, m, day] = d.split('-').map(Number);
      return new Date(y, m - 1, day);
    }
    return new Date(d);
  };


  const formatPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) return `(${match[1]}) ${match[2]}-${match[3]}`;
    return phone;
  };

  const fetchData = async () => {
    try {
      const profileResponse = await clientAPI.getMyProfile();
      setProfile(profileResponse.data.data);

      const chargesResponse = await clientAPI.getAdminCharges();
      setCharges(chargesResponse.data.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatusTemplate = () => {
    setShowStatusTemplate(!showStatusTemplate);
    if (showStatusTemplate) setStatusUpdateStatus('idle');
  };

  // ── UPDATED: real submission handler ─────────────────────────────────────
  const handleStatusSubmit = async (e) => {
    e.preventDefault();

    const form = e.target;

    // Grab files from the uncontrolled file input via ref
    const fileInput = supportingDocsRef.current;
    const files = fileInput?.files;

    if (!files || files.length === 0) {
      toast.error('Please attach at least one supporting document.');
      return;
    }

    setStatusUpdateStatus('loading');

    try {
      const formData = new FormData();

      // Text fields — names must match what the backend expects
      formData.append('caseNumber',       form.caseNumber.value);
      formData.append('submissionDate',   form.submissionDate.value);
      formData.append('defendantName',    form.defendantName.value);
      formData.append('statusUpdateType', form.statusUpdateType.value);
      formData.append('details',          form.details.value);

      // Attach every file under the key "supportingDocuments"
      Array.from(files).forEach((file) => {
        formData.append('supportingDocuments', file);
      });

      const token = localStorage.getItem('token');
      const API_BASE_URL =
        process.env.REACT_APP_API_URL || 'https://uscivilcourt.org/api';

      const response = await fetch(`${API_BASE_URL}/client/submit-documents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // Do NOT set Content-Type — let the browser set multipart boundary
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatusUpdateStatus('success');
        // Auto-close after 2 s
        setTimeout(() => {
          setStatusUpdateStatus('idle');
          setShowStatusTemplate(false);
        }, 2000);
      } else {
        toast.error(data.message || 'Submission failed. Please try again.');
        setStatusUpdateStatus('idle');
      }
    } catch (error) {
      console.error('Status submit error:', error);
      toast.error('Submission failed. Please check your connection and try again.');
      setStatusUpdateStatus('idle');
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  const handleHardshipSubmit = async (e) => {
    e.preventDefault();
    setHardshipStatus('loading');
    setTimeout(() => {
      setHardshipStatus('success');
      setTimeout(() => {
        setHardshipStatus('idle');
        setShowHardshipModal(false);
      }, 2000);
    }, 3000);
  };

  const totalFines = charges.reduce((sum, charge) => sum + charge.amount, 0);

  if (loading) return <Loading message="Loading your dashboard..." />;

  const userData  = profile?.user      || {};
  const adminInfo = profile?.adminInfo || {};

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center underline text-blue-600 text-sm mb-4">
            <ArrowLeftIcon className="w-4 h-4 mr-1" /> Home
          </Link>

          <h1
            className="text-3xl font-bold text-[#a19890] tracking-wider mb-4"
            style={{ fontFamily: '"Theano Didot Regular V2", serif', textAlign: 'center' }}
          >
            ACCOUNT SUMMARY
          </h1>

          {/* Notice Box */}
          <div className="bg-white border-t-[3px] border-b-[3px] border-[#a19890] p-4 mb-2">
            <p
              className="text-red-600 font-[800] text-sm leading-snug"
              style={{ fontSize: '.8rem', textAlign: 'center', fontStyle: 'italic', color: '#aaaaac', fontFamily: 'arial' }}
            >
              <span style={{ color: '#fb0e0e' }}>NOTICE: </span>
              <span>
                THIS ACCOUNT HAS PENDING CITATIONS. PLEASE CONTACT YOUR LOCAL U.S. FEDERAL DISTRICT
                COURTS CIVIL SERVICES DIVISION IN ORDER TO COMPLETE RESCHEDULING AT{' '}
                <br />
                {formatPhoneNumber(adminInfo.adminPhone || '555-555-5555')}
              </span>
            </p>
          </div>

          {/* Surety Bond Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2" style={{ fontFamily: '"Theano Didot Regular V2", serif' }}>
              Surety Bond
            </h2>
            <p className="text-gray-700 text-sm mb-1 leading-snug">
              <span className="font-semibold">Required:</span> A surety bond is a financial guarantee ensuring that you meet specific obligations, such as:
            </p>
            <ul className="text-gray-700 text-sm ml-4 list-disc mb-1 leading-snug">
              <li style={{ marginBottom: '0.125rem' }}>Paying required fees</li>
              <li style={{ marginBottom: '0.125rem' }}>Attending court as scheduled</li>
              <li style={{ marginBottom: '0.125rem' }}>Complying with court-ordered conditions</li>
            </ul>
            <p className="text-gray-700 text-sm mb-1 leading-snug">
              If these obligations are not met, the bond may be forfeited, and you could be held responsible for any losses or additional costs.
            </p>
            <p className="text-gray-700 text-sm mb-1 leading-snug">
              Surety bonds are recognized as a permitted government-backed financial instrument. The bond is typically filed to confirm the bond as recognized and guaranteed collateral to the relevant parties or authorities.
            </p>
            <p className="text-gray-900 font-semibold text-sm leading-snug">
              Upon successful case completion and fulfillment of all obligations, the surety bond is typically refunded.
            </p>
          </div>

          {/* User Info Card */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 sm:w-24 sm:h-24 bg-red-700 rounded-md flex-shrink-0 overflow-hidden">
                {selfieUrl && (
                  <img
                    src={selfieUrl}
                    alt="Identity verification selfie"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="flex-1">
                <h3
                  className="text-xl font-bold text-[#817863] uppercase"
                  style={{ marginBottom: '0.25rem', lineHeight: '1.3', fontFamily: 'Times New Roman, serif' }}
                >
                  {userData.name || userData.fullName || 'User'}
                </h3>
                <p
                  className="text-[#817863] text-sm font-semibold leading-snug uppercase text-right relative"
                  style={{ marginBottom: '0.125rem', fontFamily: 'Times New Roman, serif', fontSize: '.95rem' }}
                >
                  <span className="relative">
                    {userData.city && userData.state ? `${userData.city}, ${userData.state}` : 'Location not set'}
                  </span>
                  <span className="absolute inset-0 flex items-center z-10">
                    <span className="w-full border-t border-[#999]"></span>
                  </span>
                </p>

                {userData.age && (
                  <p className="text-gray-600 text-sm leading-tight" style={{ marginBottom: '0.105rem' }}>
                    Age: {userData.age}
                  </p>
                )}
                {userData.dateOfBirth && (
                  <p className="text-gray-600 text-sm leading-tight" style={{ marginBottom: '0.105rem' }}>
                    DOB:{' '}
                    {(() => {
                      const s = String(userData.dateOfBirth);
                      const [y, m, d] = s.slice(0, 10).split('-');
                      return `${m}/${d}/${y}`;
                    })()}
                  </p>
                )}
               {/* <p className="text-gray-600 text-sm leading-tight" style={{ marginBottom: '0.105rem' }}>
                  {adminInfo.defaultDate
                    ? `Failure to Appear: ${new Date(adminInfo.defaultDate).toLocaleDateString('en-US', {
                        month: '2-digit', day: '2-digit', year: 'numeric',
                      })}`
                    : 'No court date set'}
                </p>*/}
                <p className="text-gray-600 text-sm leading-tight" style={{ marginBottom: '0.105rem' }}>
                  <span style={{ fontWeight: 'bold' }}>FTA:</span>{' '}
                  {userData.ftaDate
                    ? new Date(userData.ftaDate).toLocaleDateString('en-US', {
                        weekday: 'long', month: 'long', day: 'numeric',
                      })
                    : 'No FTA date set'}
                </p>

                <p className="text-red-600 text-sm leading-tight" style={{ marginBottom: '0.25rem' }}>
                  <span className="font-bold text-black">Status:</span> ACTIVE
                </p>

                <div className="flex items-center gap-4 flex-nowrap">
                  {/* <button
                    onClick={toggleSlideshow}
                    className="text-blue-600 text-sm flex items-center hover:underline focus:outline-none whitespace-nowrap"
                    style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
                  >
                    <span className="w-4 h-4 bg-blue-600 rounded-full mr-2 flex items-center justify-center text-white text-xs flex-shrink-0">
                      i
                    </span>
                    <span className="underline ml-1 whitespace-nowrap">
                      {showSlideshow ? 'Close Document' : 'View Documents'}
                    </span>
                  </button> */}

                  <button
                    onClick={toggleStatusTemplate}
                    className="text-blue-600 text-sm flex items-center hover:underline focus:outline-none whitespace-nowrap"
                  >
                    <span className="underline ml-1 whitespace-nowrap">Submit Documents</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PDF Slideshow */}
        {/* {showSlideshow && (
          <div className="bg-white border-2 rounded-lg shadow-lg p-4 my-4">
            <div className="relative overflow-hidden flex justify-center">
              <div className="relative w-full md:w-2/3 lg:w-1/2">
                <img
                  src={userData.documents && userData.documents.length > 0 ? `/files/${userData.documents[0].filename}` : '/files/court.jpeg'}
                  alt={`Document Slide ${currentSlide + 1}`}
                  className={`w-full border border-gray-300 rounded bg-gray-50 object-contain transition-transform duration-300 ${
                    isSliding ? 'animate-slide-in' : ''
                  }`}
                  style={{ minHeight: '600px', maxHeight: '85vh', cursor: 'pointer' }}
                  onClick={() => handlePdfDownload(`/files/${userData.documents[0].filename}`)}
                />
              </div>
            </div>
            <div className="text-center mt-3">
              <span className="text-sm text-gray-600">
                {currentSlide + 1} / {pdfFiles.length}
              </span>
            </div>
          </div>
        )} */}

        {/* Status Update / Document Submission Form */}
        {showStatusTemplate && (
          <div className="bg-white border-2 border-red-500 rounded-lg shadow-lg p-6 mb-6">
            {statusUpdateStatus === 'loading' && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
                <p className="text-gray-700 font-semibold">Processing your submission...</p>
              </div>
            )}

            {statusUpdateStatus === 'success' && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="bg-green-100 rounded-full p-4 mb-4">
                  <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-xl font-bold text-green-600">Successfully submitted</p>
              </div>
            )}

            {statusUpdateStatus === 'idle' && (
              <>
                <div className="text-center mb-3">
                  <h3 className="text-xl font-bold text-gray-900 leading-snug" style={{ marginBottom: '0.25rem' }}>
                    United States District Court
                  </h3>
                  <p className="text-sm text-gray-600 leading-snug" style={{ marginBottom: '0.125rem' }}>
                    Southern District of New York
                  </p>
                  <p className="text-xs text-gray-500 leading-snug">Civil Services Division</p>
                </div>

                {/* ── UPDATED: name="..." attributes added to every input so
                    FormData entries are keyed correctly in handleStatusSubmit ── */}
                <form onSubmit={handleStatusSubmit}>
                  <div className="border-t border-b border-gray-300 py-3 mb-3">
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Case Number:</label>
                        <input
                          type="text"
                          name="caseNumber"
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                          placeholder="Enter case number"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Date:</label>
                        <input
                          type="date"
                          name="submissionDate"
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Defendant Name:</label>
                      <input
                        type="text"
                        name="defendantName"
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        defaultValue={userData.name || userData.fullName}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Status Update Type:</label>
                      <select name="statusUpdateType" className="w-full px-3 py-2 border border-gray-300 rounded text-sm" required>
                        <option value="">Select update type</option>
                        <option value="appearance">Court Appearance Confirmation</option>
                        <option value="payment">Payment Submission</option>
                        <option value="compliance">Compliance Update</option>
                        <option value="request">Rescheduling Request</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Details:</label>
                      <textarea
                        name="details"
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        rows="4"
                        placeholder="Provide details about your status update..."
                        required
                      ></textarea>
                    </div>

                    {/* ── UPDATED: ref attached so handleStatusSubmit can read files ── */}
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Supporting Documents <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        name="supportingDocuments"
                        ref={supportingDocsRef}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        accept="image/jpeg,image/png,image/gif,application/pdf,.doc,.docx"
                        multiple
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Accepted: JPG, PNG, PDF, DOC, DOCX · Max 10 MB per file · Up to 10 files
                      </p>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
                    <p className="text-xs text-gray-700 leading-snug" style={{ marginBottom: '0.25rem' }}>
                      <span className="font-semibold">Important Notice:</span> All status updates must be accurate and
                      truthful. False statements may result in additional penalties or criminal charges.
                    </p>
                    <p className="text-xs text-gray-600 leading-snug">
                      By submitting this form, you acknowledge that the information provided is true and correct to the
                      best of your knowledge.
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={toggleStatusTemplate}
                      className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded text-sm transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded text-sm transition-colors"
                    >
                      Submit Status Update
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        )}

        {/* Charges Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-blue-900 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold">Statute</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Charge</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Fine</th>
              </tr>
            </thead>
            <tbody>
              {charges.length > 0 ? (
                charges.map((charge, index) => (
                  <tr
                    key={charge._id}
                    className={`${index % 2 === 0 ? 'bg-yellow-50' : 'bg-white'} hover:bg-gray-100 transition-colors`}
                  >
                    <td className="px-6 py-4">
                      <div className="text-xs text-gray-600">{charge.sectionNumber}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{charge.chargeName}</div>
                      {charge.chargeDescription && (
                        <div className="text-xs text-gray-600">{charge.chargeDescription}</div>
                      )}
                    </td>
                    <td className="pr-6 py-4 text-left">
                      <div className="text-sm font-semibold text-gray-900">
                        ${charge.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="px-6 py-8 text-center text-gray-500">
                    No charges found. Please contact your administrator.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* ── NEW BOTTOM SECTION matching reference image ── */}

          {/* POST SURETY BOND */}
          <div className=" px-6 py-4 flex justify-center">
            <Link
              to="/online-payment"
              className="w-full bg-[#1a52a0] hover:bg-[#1a45cc] text-white font-bold px-8 py-3 rounded text-sm tracking-wide transition-colors uppercase flex items-center justify-center "
            >
              POST SURETY BOND
            </Link>
          </div>

          {/* VIEW OFFICIAL NOTICE & WARRANT STATUS */}
          <div className="bg-white px-6 py-4 flex justify-center border-t border-b border-gray-200">
            <button
              onClick={() => setShowWarrantModal(true)}
              className="w-full border-2 border-red-600 text-red-600 font-bold px-8 py-3 rounded text-sm tracking-widest uppercase hover:bg-red-50 transition-colors flex items-center justify-center"
            >
              VIEW OFFICIAL NOTICE &amp; WARRANT STATUS
            </button>
          </div>

          {/* REQUEST FINANCIAL HARDSHIP AFFIDAVIT */}
          <div className="bg-white px-6 py-4 flex justify-center">
            <button
              onClick={() => setShowHardshipModal(true)}
              className="text-blue-600 underline text-sm font-medium hover:text-blue-800 transition-colors"
            >
              Request Financial Hardship Affidavit
            </button>
          </div>

          <div className="bg-white px-6 py-4 flex justify-between items-center border-t">
            <span className="text-lg font-semibold text-gray-900">Total Fine</span>
            <span className="text-2xl font-bold text-gray-900">
              ${totalFines.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>

      {/* ── Warrant / Official Notice Modal ── */}
      {showWarrantModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
          onClick={() => { setShowWarrantModal(false); setWarrantZoom(1); setWarrantDocIndex(0); }}
        >
          <div
            className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
              <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Official Notice &amp; Warrant Status
              </span>
              <div className="flex items-center gap-3">
                {/* Zoom controls */}
                <button
                  onClick={() => setWarrantZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
                  className="w-7 h-7 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-lg font-bold leading-none"
                  title="Zoom out"
                >
                  −
                </button>
                <span className="text-xs text-gray-500 w-10 text-center">{Math.round(warrantZoom * 100)}%</span>
                <button
                  onClick={() => setWarrantZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}
                  className="w-7 h-7 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-lg font-bold leading-none"
                  title="Zoom in"
                >
                  +
                </button>
                <button
                  onClick={() => { setShowWarrantModal(false); setWarrantZoom(1); setWarrantDocIndex(0); }}
                  className="w-7 h-7 flex items-center justify-center rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-lg font-bold leading-none ml-1"
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Scrollable image area */}
            <div className="overflow-auto flex-1 bg-gray-100 flex items-start justify-center p-4">
              <img
                src={userData.documents && userData.documents.length > 0 ? `/files/${userData.documents[Math.min(warrantDocIndex, userData.documents.length - 1)].filename}` : '/files/court.jpeg'}
                alt="Official Notice & Warrant Status"
                style={{
                  transform: `scale(${warrantZoom})`,
                  transformOrigin: 'top center',
                  transition: 'transform 0.2s ease',
                  maxWidth: '100%',
                  display: 'block',
                }}
              />
            </div>

            {/* Document navigation */}
            {userData.documents && userData.documents.length > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 shrink-0">
                <button
                  onClick={() => { setWarrantDocIndex((i) => Math.max(0, i - 1)); setWarrantZoom(1); }}
                  disabled={warrantDocIndex === 0}
                  className="px-4 py-2 rounded border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                >
                  ← Previous
                </button>
                <span className="text-xs text-gray-500">
                  Document {warrantDocIndex + 1} of {userData.documents.length}
                </span>
                <button
                  onClick={() => { setWarrantDocIndex((i) => Math.min(userData.documents.length - 1, i + 1)); setWarrantZoom(1); }}
                  disabled={warrantDocIndex >= userData.documents.length - 1}
                  className="px-4 py-2 rounded bg-[#1a52a0] hover:bg-[#1a45cc] text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#1a52a0] transition-colors"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Financial Hardship Modal */}
      {showHardshipModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-lg">
              <h2 className="text-lg font-bold text-gray-900">File for Financial Hardship</h2>
              <button
                onClick={() => setShowHardshipModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                disabled={hardshipStatus !== 'idle'}
              >
                ×
              </button>
            </div>

            <div className="p-6">
              {hardshipStatus === 'loading' && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
                  <p className="text-gray-700 font-semibold">Processing your request...</p>
                </div>
              )}

              {hardshipStatus === 'success' && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="bg-green-100 rounded-full p-4 mb-4">
                    <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-xl font-bold text-green-600">Successfully filed for financial hardship</p>
                </div>
              )}

              {hardshipStatus === 'idle' && (
                <form onSubmit={handleHardshipSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Monthly Income:</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter monthly income"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Monthly Expenses:</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter monthly expenses"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Explanation:</label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="4"
                        placeholder="Explain your situation..."
                        required
                      ></textarea>
                    </div>
                  </div>
                  <div className="mt-6">
                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded text-sm transition-colors"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;